import moment from 'moment';

import {
	PENDING_STOCK_SOURCE_TYPE,
	PENDING_STOCK_SOURCE_TYPE_NAMES,
} from '../../../common/constants';
import { getLocalTime } from '../../../common/utils/time/time';
import {
	normalizeCalendarDateKey,
	normalizeDateKey,
	serializeDateKeyForTimezone,
} from '../../../common/utils/warehousing/formatExpiryBatchSummary';

export const SHORT_DATED_DAYS = 60;

export const normalizeQuantity = value => Math.max(0, Math.round(Number(value) || 0));

export const getDriverName = (driver) => {
	if (!driver) {
		return 'n/a';
	}

	return [driver.firstname, driver.lastname].filter(Boolean).join(' ') || driver.username || 'Unnamed driver';
};

const getExpiryDaysRemaining = (expiryDate, timezone) => {
	if (!expiryDate) {
		return null;
	}

	const parsedDate = moment(expiryDate, 'YYYY-MM-DD', true);
	if (!parsedDate.isValid()) {
		return null;
	}

	const currentDateKey = getLocalTime(timezone).format('YYYY-MM-DD');
	return parsedDate.diff(moment(currentDateKey, 'YYYY-MM-DD', true), 'days');
};

const toDateKeyOrEmpty = (value, timezone) => normalizeDateKey(value, timezone) || '';

export const buildInitialBatchRows = (pendingStock, quantity, timezone) => {
	const normalizedQuantity = normalizeQuantity(quantity);
	const fallbackExpiryDate = toDateKeyOrEmpty(pendingStock?.expiryDate, timezone);
	const batchExpiryDates = pendingStock?.batchExpiryDates || {};
	const positionKeys = Object.keys(batchExpiryDates)
		.map(Number)
		.filter(key => Number.isInteger(key) && key > 0);
	const legacySharedExpiryDate = positionKeys.length === 1 && positionKeys[0] === 1
		? toDateKeyOrEmpty(batchExpiryDates[1], timezone)
		: '';

	if (!normalizedQuantity) {
		return [{ quantity: null, expiryDate: '' }];
	}

	if (legacySharedExpiryDate) {
		return [{
			quantity: normalizedQuantity,
			expiryDate: legacySharedExpiryDate,
		}];
	}

	if (!positionKeys.length) {
		return [{
			quantity: normalizedQuantity,
			expiryDate: fallbackExpiryDate,
		}];
	}

	const rows = [];
	for (let i = 1; i <= normalizedQuantity; i += 1) {
		const expiryDate = toDateKeyOrEmpty(batchExpiryDates[i], timezone);
		const previousRow = rows[rows.length - 1];
		if (previousRow && previousRow.expiryDate === expiryDate) {
			previousRow.quantity += 1;
		}
		else {
			rows.push({
				quantity: 1,
				expiryDate,
			});
		}
	}

	return rows.length ? rows : [{ quantity: normalizedQuantity, expiryDate: fallbackExpiryDate }];
};

export const buildDefaultBatchRows = quantity => ([{
	quantity: normalizeQuantity(quantity) || null,
	expiryDate: '',
}]);

const normalizeBatchRows = (rows = []) => {
	return (rows || [])
		.map(row => ({
			quantity: normalizeQuantity(row?.quantity),
			expiryDate: normalizeCalendarDateKey(row?.expiryDate) || '',
		}))
		.filter(row => row.quantity > 0);
};

export const getBatchRowsQuantity = rows => normalizeBatchRows(rows)
	.reduce((total, row) => total + row.quantity, 0);

export const getFirstBatchExpiryDate = (rows = []) => {
	const firstRowWithExpiryDate = normalizeBatchRows(rows)
		.find(row => row.expiryDate);

	return firstRowWithExpiryDate?.expiryDate || '';
};

export const normalizeBatchExpiryDatesForSave = (rows = [], timezone) => {
	const normalizedDates = {};
	let unitIndex = 1;

	normalizeBatchRows(rows).forEach(row => {
		for (let i = 0; i < row.quantity; i += 1) {
			normalizedDates[unitIndex] = serializeDateKeyForTimezone(row.expiryDate, timezone);
			unitIndex += 1;
		}
	});

	return Object.keys(normalizedDates).length ? normalizedDates : null;
};

export const hasShortDatedBatchExpiryDate = (rows = [], timezone) => {
	for (const row of normalizeBatchRows(rows)) {
		const expiryDaysRemaining = getExpiryDaysRemaining(row.expiryDate, timezone);
		if (expiryDaysRemaining !== null && expiryDaysRemaining < SHORT_DATED_DAYS) {
			return true;
		}
	}

	return false;
};

export const hasExpiredBatchExpiryDate = (rows = [], timezone) => {
	for (const row of normalizeBatchRows(rows)) {
		const expiryDaysRemaining = getExpiryDaysRemaining(row.expiryDate, timezone);
		if (expiryDaysRemaining !== null && expiryDaysRemaining <= 0) {
			return true;
		}
	}

	return false;
};

export const hasUnknownBatchExpiryDate = (rows = []) => (
	normalizeBatchRows(rows).some(row => !row.expiryDate)
);

export const formatSourceLabel = (item) => {
	if (item?.sourceType === PENDING_STOCK_SOURCE_TYPE.DELIVERY) {
		const identifier = item?.stockDelivery?.identifier;
		return identifier ? `Delivery ${identifier}` : PENDING_STOCK_SOURCE_TYPE_NAMES[PENDING_STOCK_SOURCE_TYPE.DELIVERY];
	}

	if (item?.sourceType === PENDING_STOCK_SOURCE_TYPE.REMOVED) {
		return item?.machine?.assetId
			? `Removed from ${item.machine.assetId}`
			: PENDING_STOCK_SOURCE_TYPE_NAMES[PENDING_STOCK_SOURCE_TYPE.REMOVED];
	}

	return PENDING_STOCK_SOURCE_TYPE_NAMES[item?.sourceType] || 'Unknown source';
};
